import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { isAfter, sub } from "date-fns/esm";
import groupBy from "lodash-es/groupBy";

import { GuideStatus } from "@mapmycustomers/shared/enum/guides";
import { GuideUserStatus } from "@mapmycustomers/shared/types/entity";
import Role from "@mapmycustomers/shared/types/Role";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";

import Subheader from "@app/scene/onboarding/components/Subheader";
import ViewMode from "@app/scene/onboarding/enums/ViewMode";
import { getGuidesStatus, isLoading } from "@app/scene/onboarding/store";
import { initialize, refreshGuidesStatus } from "@app/scene/onboarding/store/actions";
import { DateFilterValue } from "@app/scene/onboarding/types/DateUnit";
import getUsersStatusMap from "@app/scene/onboarding/utils/getUsersStatusMap";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import useInterval from "@app/util/hook/useInterval";
import { parseApiDate } from "@app/util/parsers";

import BulkActions from "./components/OnboardingUsers/BulkActions";
import Users from "./components/OnboardingUsers/Users";
import UsersFilterBar from "./components/OnboardingUsers/UsersFilterBar";
import UsersStats from "./components/OnboardingUsers/UsersStats";
import styles from "./OnboardingPageCommon.module.scss";

interface Props {
  guidesStatus: GuideUserStatus[];
  loading?: boolean;
  onInitialize: () => void;
  onRefreshGuideStatus: () => void;
  users: User[];
}

export const OnboardingUsers: React.FC<Props> = ({
  guidesStatus,
  loading,
  onInitialize,
  onRefreshGuideStatus,
  users,
}) => {
  useEffect(() => {
    onInitialize();
  }, [onInitialize]);

  useInterval(onRefreshGuideStatus, true, 15_000);

  const usersStatusMap = useMemo(() => getUsersStatusMap(guidesStatus), [guidesStatus]);

  const [selectedUserIds, setSelectedUserIds] = useState<User["id"][]>([]);
  const handleResetSelection = useCallback(() => setSelectedUserIds([]), []);

  const [teamFilter, setTeamFilter] = useState<Team["id"] | undefined>();
  const [roleFilter, setRoleFilter] = useState<Role["id"] | undefined>();
  const [quickFilter, setQuickFilter] = useState<string>("");
  const [completionFilter, setCompletionFilter] = useState<GuideStatus | undefined>();
  const [creationFilter, setCreationFilter] = useState<DateFilterValue | undefined>();

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const filter = quickFilter.trim().toLowerCase();
      if (
        (filter &&
          !user.fullName?.toLowerCase().includes(filter) &&
          !user.username.toLowerCase().includes(filter)) ||
        (teamFilter && !user.teams.some(({ id }) => teamFilter === id)) ||
        (roleFilter && user.role.id !== roleFilter)
      ) {
        return false;
      }

      if (completionFilter) {
        const statuses = usersStatusMap[user.id];
        if (!statuses) {
          return false;
        }
        const groups = groupBy(statuses, "status") as Partial<
          Record<GuideStatus, GuideUserStatus[]>
        >;
        if (
          ([GuideStatus.COMPLETED, GuideStatus.NOT_STARTED].includes(completionFilter) &&
            groups[completionFilter]?.length !== statuses.length) ||
          (completionFilter === GuideStatus.PROGRESS &&
            (groups[GuideStatus.COMPLETED]?.length === statuses.length ||
              groups[GuideStatus.NOT_STARTED]?.length === statuses.length))
        ) {
          return false;
        }
      }

      if (creationFilter) {
        const date = sub(new Date(), { [creationFilter.unit]: creationFilter.amount });
        const userDate = parseApiDate(user.createdAt);
        if (!isAfter(userDate, date)) {
          return false;
        }
      }

      return true;
    });
  }, [
    users,
    quickFilter,
    teamFilter,
    roleFilter,
    completionFilter,
    creationFilter,
    usersStatusMap,
  ]);

  return (
    <div className={styles.container}>
      <Subheader
        totalFilteredRecords={filteredUsers.length}
        totalRecords={users.length}
        viewMode={ViewMode.USERS}
      />
      <div className={styles.content}>
        <UsersFilterBar
          completionFilter={completionFilter}
          creationFilter={creationFilter}
          onCompletionFilterChange={setCompletionFilter}
          onCreationFilterChange={setCreationFilter}
          onQuickFilterChange={setQuickFilter}
          onRoleFilterChange={setRoleFilter}
          onTeamFilterChange={setTeamFilter}
          quickFilter={quickFilter}
          roleFilter={roleFilter}
          teamFilter={teamFilter}
        />
        <BulkActions onResetSelection={handleResetSelection} userIds={selectedUserIds} />
        <div className={styles.delimiter} />
        <UsersStats users={filteredUsers} usersStatusMap={usersStatusMap} />
        <Users
          loading={loading}
          onSelectionChange={setSelectedUserIds}
          selectedUserIds={selectedUserIds}
          users={filteredUsers}
          usersStatus={usersStatusMap}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  guidesStatus: getGuidesStatus(state),
  loading: isLoading(state),
  users: getUsers(state),
});

const mapDispatchToProps = {
  onInitialize: initialize.request,
  onRefreshGuideStatus: refreshGuidesStatus.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(OnboardingUsers);
